import React, { useEffect, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { ChevronDown, Home, Lock } from "lucide-react";
import useServiceModules from "@/modules/services/hooks/useServiceModules";

// "compact" rows for the desktop menu, taller "touch" rows for the mobile drawer.
const ROW_SIZES = {
  compact: "min-h-10 py-1.5",
  touch: "min-h-12 py-2",
};

// Collapsible sections of the menu, by module key, in display order.
const NAV_GROUPS = [
  {
    key: "tenders",
    labelKey: "nav_group_tender_management",
    modules: ["tenders_offers_management", "offers_management", "approvals_requests_management", "proposal_approvals_management"],
  },
  {
    key: "offers",
    labelKey: "nav_group_offers_award",
    modules: ["incoming_offers_price_analysis_management", "negotiation_award_management"],
  },
  {
    key: "vendors",
    labelKey: "nav_group_vendors_companies",
    modules: ["preferred_vendors_management", "Importing_Entity_Plan", "companies_management", "work_classification_management"],
  },
  {
    key: "contracts",
    labelKey: "nav_group_contracts_financial",
    modules: ["contracts_management", "financial_claims_management"],
  },
  {
    key: "account",
    labelKey: "nav_group_account_admin",
    modules: ["subscription_management", "delegation_permissions_management", "communication_management", "notifications_management"],
  },
  {
    key: "help",
    labelKey: "nav_group_help_support",
    modules: ["platform_guide"],
  },
];

// Vertical list of Tarh services with the same subscribed / locked behaviour as the Services page.
// The current service is highlighted; choosing one runs the usual access check, then onNavigate closes the menu.
export default function ServicesNavList({ activeKey, onNavigate, size = "compact" }) {
  const { t } = useTranslation();
  const { modules, openModule } = useServiceModules();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const labelId = useId();
  const groupIdPrefix = useId();

  const byKey = new Map(modules.map((module) => [module.key, module]));
  const groups = NAV_GROUPS.map((group) => ({
    ...group,
    items: group.modules.map((key) => byKey.get(key)).filter(Boolean),
  })).filter((group) => group.items.length > 0);
  // A module missing from NAV_GROUPS still shows, after the sections, instead of disappearing.
  const grouped = new Set(NAV_GROUPS.flatMap((group) => group.modules));
  const ungrouped = modules.filter((module) => !grouped.has(module.key));

  // The section holding the current service opens by itself; the others stay as the reader left them.
  const activeGroupKey = groups.find((group) => group.items.some((module) => module.key === activeKey))?.key;
  const [openGroups, setOpenGroups] = useState(() => new Set([activeGroupKey ?? NAV_GROUPS[0].key]));
  useEffect(() => {
    if (activeGroupKey) setOpenGroups((current) => (current.has(activeGroupKey) ? current : new Set(current).add(activeGroupKey)));
  }, [activeGroupKey]);
  const toggleGroup = (key) =>
    setOpenGroups((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const renderItem = (module) => {
    const { Icon, isAvailable } = module;
    const isActive = module.to ? pathname === module.to : module.key === activeKey;

    return (
      <li key={module.key}>
        <button
          type="button"
          aria-current={isActive ? "page" : undefined}
          onClick={() => {
            onNavigate?.();
            if (module.to) navigate(module.to);
            else openModule(module);
          }}
          className={`group relative flex w-full items-center gap-3 rounded-lg px-2.5 text-start text-sm transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-(--primary-green)/40 ${
            ROW_SIZES[size] ?? ROW_SIZES.compact
          } ${
            isActive
              ? "bg-emerald-50 font-semibold text-(--primary-green) dark:bg-emerald-500/10 dark:text-emerald-400"
              : isAvailable
                ? "font-medium text-slate-700 hover:bg-slate-100 hover:text-slate-900 active:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-700 dark:hover:text-white dark:active:bg-slate-700"
                : "font-medium text-slate-400 hover:bg-slate-50 active:bg-slate-50 dark:text-slate-500 dark:hover:bg-slate-700/50"
          }`}
        >
          {isActive && (
            <span aria-hidden="true" className="absolute inset-y-2 start-0 w-[3px] rounded-full bg-(--primary-green)" />
          )}
          <span
            aria-hidden="true"
            className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg transition-colors duration-150 ${
              isActive
                ? "bg-(--primary-green) text-white shadow-sm"
                : isAvailable
                  ? "bg-emerald-50 text-(--primary-green) group-hover:bg-white dark:bg-emerald-500/10 dark:text-emerald-400 dark:group-hover:bg-slate-800"
                  : "bg-slate-100 text-slate-400 dark:bg-slate-700 dark:text-slate-500"
            }`}
          >
            <Icon size={16} strokeWidth={2} />
          </span>
          <span className="line-clamp-2 min-w-0 flex-1 leading-snug">{module.label}</span>
          {module.freeBadge && (
            <span className="shrink-0 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-bold leading-4 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300">
              {t("free")}
            </span>
          )}
          {!isAvailable && (
            <Lock
              size={14}
              strokeWidth={2.25}
              className="shrink-0 text-slate-400 dark:text-slate-500"
              aria-label={t("services_status_not_subscribed")}
            />
          )}
        </button>
      </li>
    );
  };

  return (
    <nav aria-labelledby={labelId}>
      <p
        id={labelId}
        className="px-2.5 pb-1.5 pt-1 text-[11px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500"
      >
        {t("services_main_dashboard_title")}
      </p>
      {/* The All Tenders landing page comes first; it is open to every subscriber, so it is never locked. */}
      <ul className="space-y-0.5">
        {renderItem({ key: "tenders-landing", label: t("All_Tenders"), Icon: Home, isAvailable: true, to: "/tenders" })}
      </ul>

      {groups.map((group) => {
        const open = openGroups.has(group.key);
        const panelId = `${groupIdPrefix}-${group.key}`;
        return (
          <div key={group.key} className="mt-2 border-t border-slate-100 pt-2 dark:border-slate-700/60">
            <button
              type="button"
              aria-expanded={open}
              aria-controls={panelId}
              onClick={() => toggleGroup(group.key)}
              className="flex w-full items-center justify-between gap-2 rounded-md px-2.5 py-1.5 text-start text-xs font-bold uppercase tracking-wide text-slate-700 transition-colors rtl:tracking-normal duration-150 hover:bg-slate-50 hover:text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-(--primary-green)/40 motion-reduce:transition-none dark:text-slate-200 dark:hover:bg-slate-700/50 dark:hover:text-white"
            >
              <span className="min-w-0 truncate">{t(group.labelKey)}</span>
              <ChevronDown
                size={14}
                aria-hidden="true"
                className={`shrink-0 transition-transform duration-200 motion-reduce:transition-none ${open ? "rotate-180" : ""}`}
              />
            </button>
            {/* Height and fade animate together (grid rows 0fr ⇄ 1fr); a closed section can't be tabbed into. */}
            <div
              id={panelId}
              inert={!open}
              className={`grid transition-[grid-template-rows,opacity] duration-200 ease-out motion-reduce:transition-none ${
                open ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
              }`}
            >
              {/* -m/p keep the rows' focus ring visible inside the clipping box. */}
              <ul className="-m-0.5 min-h-0 space-y-0.5 overflow-hidden p-0.5 pt-1">
                {group.items.map(renderItem)}
              </ul>
            </div>
          </div>
        );
      })}

      {ungrouped.length > 0 && <ul className="mt-2 space-y-0.5 border-t border-slate-100 pt-2 dark:border-slate-700/60">{ungrouped.map(renderItem)}</ul>}
    </nav>
  );
}
